import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMobileStands, updateMobileStand } from '../services/mobileStandService';
import { getPortions, updatePortion } from '../services/portionService';
import { getMenuProducts, updateMenuProduct } from '../services/menuProductService';
import { getPortionStockByStand } from '../services/mobileStandPortionService';
import { getPortionSuppliesByStand } from '../services/portionSupplyService';
import { formatCurrency } from '../utils/format';
import { resolveImageUrl } from '../utils/media';
import MobileStandForm from '../components/MobileStandForm';
import PortionForm from '../components/PortionForm';
import MenuProductForm from '../components/MenuProductForm';
import MenuProductRecipeModal from '../components/MenuProductRecipeModal';
import MobileStandPortionModal from '../components/MobileStandPortionModal';
import PortionSupplyFormModal from '../components/PortionSupplyFormModal';
import PortionSupplyDetalleModal from '../components/PortionSupplyDetalleModal';

const TABS = [
  { key: 'stands', label: 'Stands', icon: 'bi-shop-window' },
  { key: 'menu', label: 'Menú', icon: 'bi-egg-fried' },
  { key: 'portions', label: 'Porciones', icon: 'bi-box-seam' },
  { key: 'inventory', label: 'Inventario', icon: 'bi-clipboard-data' },
  { key: 'supplies', label: 'Abastecimientos', icon: 'bi-truck' },
];

function TabButton({ tab, active, onClick }) {
  return (
    <button
      className="btn-orig-ghost"
      onClick={onClick}
      style={{
        padding: '10px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
        color: active ? 'var(--orig-green-bright)' : 'var(--orig-text-muted)',
        borderBottom: active ? '2px solid var(--orig-green)' : '2px solid transparent',
        marginBottom: -1, whiteSpace: 'nowrap',
      }}
    >
      <i className={`bi ${tab.icon}`} style={{ marginRight: 6 }} />
      {tab.label}
    </button>
  );
}

function LoadingRow({ label }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
      <i className="bi bi-arrow-repeat" style={{ fontSize: 28, display: 'block', marginBottom: 10, animation: 'spin 1s linear infinite' }} />
      {label}
    </div>
  );
}

function ErrorRow({ message }) {
  return (
    <div style={{ padding: '28px 20px' }}>
      <div className="alert-orig alert-error">
        <i className="bi bi-exclamation-triangle-fill" />
        {message}
      </div>
    </div>
  );
}

function EmptyRow({ icon, title, subtitle }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
      <i className={`bi ${icon}`} style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12 }}>{subtitle}</div>
    </div>
  );
}

export default function StandMovil() {
  const [activeTab, setActiveTab] = useState('stands');
  const [selectedStandId, setSelectedStandId] = useState('');

  // Stands
  const [stands, setStands] = useState([]);
  const [loadingStands, setLoadingStands] = useState(true);
  const [standsError, setStandsError] = useState('');
  const [showStandForm, setShowStandForm] = useState(false);
  const [editingStand, setEditingStand] = useState(null);
  const [standActionError, setStandActionError] = useState('');
  const [standBusy, setStandBusy] = useState(false);

  // Menu products
  const [menuProducts, setMenuProducts] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [menuError, setMenuError] = useState('');
  const [showMenuForm, setShowMenuForm] = useState(false);
  const [editingMenuProduct, setEditingMenuProduct] = useState(null);
  const [menuActionError, setMenuActionError] = useState('');
  const [menuBusy, setMenuBusy] = useState(false);
  const [recipeProduct, setRecipeProduct] = useState(null);

  // Portions
  const [portions, setPortions] = useState([]);
  const [loadingPortions, setLoadingPortions] = useState(true);
  const [portionsError, setPortionsError] = useState('');
  const [showPortionForm, setShowPortionForm] = useState(false);
  const [editingPortion, setEditingPortion] = useState(null);
  const [portionActionError, setPortionActionError] = useState('');
  const [portionBusy, setPortionBusy] = useState(false);

  // Stand portion stock (inventory tab)
  const [stock, setStock] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockError, setStockError] = useState('');
  const [showStockModal, setShowStockModal] = useState(false);
  const [editingStock, setEditingStock] = useState(null);

  // Portion supplies
  const [supplies, setSupplies] = useState([]);
  const [loadingSupplies, setLoadingSupplies] = useState(false);
  const [suppliesError, setSuppliesError] = useState('');
  const [showSupplyForm, setShowSupplyForm] = useState(false);
  const [viewingSupply, setViewingSupply] = useState(null);

  const loadStands = useCallback(() => {
    setLoadingStands(true);
    setStandsError('');
    return getMobileStands()
      .then(data => setStands(Array.isArray(data) ? data : []))
      .catch(() => setStandsError('No se pudo cargar el listado de stands móviles.'))
      .finally(() => setLoadingStands(false));
  }, []);

  const loadMenuProducts = useCallback(() => {
    setLoadingMenu(true);
    setMenuError('');
    return getMenuProducts()
      .then(data => setMenuProducts(Array.isArray(data) ? data : []))
      .catch(() => setMenuError('No se pudo cargar el listado de productos del menú.'))
      .finally(() => setLoadingMenu(false));
  }, []);

  const loadPortions = useCallback(() => {
    setLoadingPortions(true);
    setPortionsError('');
    return getPortions()
      .then(data => setPortions(Array.isArray(data) ? data : []))
      .catch(() => setPortionsError('No se pudo cargar el listado de porciones.'))
      .finally(() => setLoadingPortions(false));
  }, []);

  const loadStock = useCallback((standId) => {
    if (!standId) { setStock([]); return; }
    setLoadingStock(true);
    setStockError('');
    getPortionStockByStand(standId)
      .then(data => setStock(Array.isArray(data) ? data : []))
      .catch(() => setStockError('No se pudo cargar el inventario de porciones del stand.'))
      .finally(() => setLoadingStock(false));
  }, []);

  const loadSupplies = useCallback((standId) => {
    if (!standId) { setSupplies([]); return; }
    setLoadingSupplies(true);
    setSuppliesError('');
    getPortionSuppliesByStand(standId)
      .then(data => setSupplies(Array.isArray(data) ? data : []))
      .catch(() => setSuppliesError('No se pudo cargar el historial de abastecimientos.'))
      .finally(() => setLoadingSupplies(false));
  }, []);

  useEffect(() => { loadStands(); }, [loadStands]);
  useEffect(() => { loadMenuProducts(); }, [loadMenuProducts]);
  useEffect(() => { loadPortions(); }, [loadPortions]);
  useEffect(() => { loadStock(selectedStandId); }, [selectedStandId, loadStock]);
  useEffect(() => { loadSupplies(selectedStandId); }, [selectedStandId, loadSupplies]);

  const activeStands = useMemo(() => stands.filter(s => s.state), [stands]);
  const selectedStand = stands.find(s => String(s.id) === String(selectedStandId));

  // --- Stands actions ---
  function openCreateStand() { setEditingStand(null); setShowStandForm(true); }
  function openEditStand(stand) { setEditingStand(stand); setShowStandForm(true); }
  function closeStandForm() { setShowStandForm(false); setEditingStand(null); }
  function handleStandSaved() { closeStandForm(); loadStands(); }

  async function toggleStandState(stand) {
    setStandActionError('');
    setStandBusy(true);
    try {
      await updateMobileStand(stand.id, { name: stand.name, location: stand.location, state: !stand.state });
      await loadStands();
    } catch (err) {
      setStandActionError(err.response?.data?.msg || 'No se pudo cambiar el estado del stand.');
    } finally {
      setStandBusy(false);
    }
  }

  // --- Menu actions ---
  function openCreateMenuProduct() { setEditingMenuProduct(null); setShowMenuForm(true); }
  function openEditMenuProduct(product) { setEditingMenuProduct(product); setShowMenuForm(true); }
  function closeMenuForm() { setShowMenuForm(false); setEditingMenuProduct(null); }
  function handleMenuSaved() { closeMenuForm(); loadMenuProducts(); }

  async function toggleMenuProductState(product) {
    setMenuActionError('');
    setMenuBusy(true);
    try {
      await updateMenuProduct(product.id, { name: product.name, price: product.price, state: !product.state });
      await loadMenuProducts();
    } catch (err) {
      setMenuActionError(err.response?.data?.msg || 'No se pudo cambiar el estado del producto.');
    } finally {
      setMenuBusy(false);
    }
  }

  // --- Portions actions ---
  function openCreatePortion() { setEditingPortion(null); setShowPortionForm(true); }
  function openEditPortion(portion) { setEditingPortion(portion); setShowPortionForm(true); }
  function closePortionForm() { setShowPortionForm(false); setEditingPortion(null); }
  function handlePortionSaved() { closePortionForm(); loadPortions(); }

  async function togglePortionState(portion) {
    setPortionActionError('');
    setPortionBusy(true);
    try {
      await updatePortion(portion.id, { name: portion.name, unitType: portion.unitType, state: !portion.state });
      await loadPortions();
    } catch (err) {
      setPortionActionError(err.response?.data?.msg || 'No se pudo cambiar el estado de la porción.');
    } finally {
      setPortionBusy(false);
    }
  }

  // --- Inventory (stock) actions ---
  function openAddStock() { setEditingStock(null); setShowStockModal(true); }
  function openEditStock(item) { setEditingStock(item); setShowStockModal(true); }
  function closeStockModal() { setShowStockModal(false); setEditingStock(null); }
  function handleStockSaved() { closeStockModal(); loadStock(selectedStandId); }

  // --- Supplies actions ---
  function handleSupplySaved() { setShowSupplyForm(false); loadSupplies(selectedStandId); loadStock(selectedStandId); }

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
          Menú, porciones e inventario del stand móvil
        </p>
        <h5 style={{
          fontWeight: 800, color: 'var(--orig-text)',
          fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
        }}>
          Stand Móvil
        </h5>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--orig-border)', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <TabButton key={tab.key} tab={tab} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} />
        ))}
      </div>

      {/* Tab: Stands */}
      {activeTab === 'stands' && (
        <>
          <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-orig btn-orig-green" onClick={openCreateStand}>
              <i className="bi bi-plus-lg" />
              Nuevo Stand
            </button>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h6>
                <i className="bi bi-shop-window" style={{ color: 'var(--orig-green)' }} />
                Stands móviles
              </h6>
              {!loadingStands && (
                <span className="badge-orig badge-green">
                  {stands.length} {stands.length === 1 ? 'stand' : 'stands'}
                </span>
              )}
            </div>

            {standActionError && (
              <div style={{ padding: '14px 20px 0' }}>
                <div className="alert-orig alert-error">
                  <i className="bi bi-exclamation-triangle-fill" />
                  {standActionError}
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              {loadingStands ? (
                <LoadingRow label="Cargando stands..." />
              ) : standsError ? (
                <ErrorRow message={standsError} />
              ) : stands.length === 0 ? (
                <EmptyRow icon="bi-shop-window" title="Sin resultados" subtitle="No hay stands móviles registrados." />
              ) : (
                <table className="orig-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nombre</th>
                      <th>Ubicación</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stands.map(s => (
                      <tr key={s.id}>
                        <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>{s.id}</td>
                        <td style={{ fontWeight: 500 }}>{s.name}</td>
                        <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{s.location || '—'}</td>
                        <td>
                          <span className={`badge-orig ${s.state ? 'badge-green' : 'badge-red'}`}>
                            <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                            {s.state ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => openEditStand(s)}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Editar stand"
                            >
                              <i className="bi bi-pencil" />
                              Editar
                            </button>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => toggleStandState(s)}
                              disabled={standBusy}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Cambiar estado"
                            >
                              <i className="bi bi-toggle2-on" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Tab: Menú */}
      {activeTab === 'menu' && (
        <>
          <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-orig btn-orig-green" onClick={openCreateMenuProduct}>
              <i className="bi bi-plus-lg" />
              Nuevo Producto
            </button>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h6>
                <i className="bi bi-egg-fried" style={{ color: 'var(--orig-green)' }} />
                Productos del menú
              </h6>
              {!loadingMenu && (
                <span className="badge-orig badge-green">
                  {menuProducts.length} {menuProducts.length === 1 ? 'producto' : 'productos'}
                </span>
              )}
            </div>

            {menuActionError && (
              <div style={{ padding: '14px 20px 0' }}>
                <div className="alert-orig alert-error">
                  <i className="bi bi-exclamation-triangle-fill" />
                  {menuActionError}
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              {loadingMenu ? (
                <LoadingRow label="Cargando productos..." />
              ) : menuError ? (
                <ErrorRow message={menuError} />
              ) : menuProducts.length === 0 ? (
                <EmptyRow icon="bi-egg-fried" title="Sin resultados" subtitle="No hay productos registrados en el menú." />
              ) : (
                <table className="orig-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Producto</th>
                      <th>Precio</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {menuProducts.map(p => (
                      <tr key={p.id}>
                        <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>{p.id}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: 6, flexShrink: 0,
                              border: '1px solid var(--orig-border)', background: 'var(--orig-panel)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                            }}>
                              {p.image ? (
                                <img src={resolveImageUrl(p.image)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <i className="bi bi-image" style={{ fontSize: 15, color: 'var(--orig-text-hint)' }} />
                              )}
                            </div>
                            <span style={{ fontWeight: 500 }}>{p.name}</span>
                          </div>
                        </td>
                        <td style={{ fontSize: 13, fontWeight: 600 }}>{formatCurrency(p.price)}</td>
                        <td>
                          <span className={`badge-orig ${p.state ? 'badge-green' : 'badge-red'}`}>
                            <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                            {p.state ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => openEditMenuProduct(p)}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Editar producto"
                            >
                              <i className="bi bi-pencil" />
                              Editar
                            </button>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => setRecipeProduct(p)}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Administrar receta"
                            >
                              <i className="bi bi-egg-fried" />
                              Receta
                            </button>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => toggleMenuProductState(p)}
                              disabled={menuBusy}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Cambiar estado"
                            >
                              <i className="bi bi-toggle2-on" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Tab: Porciones */}
      {activeTab === 'portions' && (
        <>
          <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-orig btn-orig-green" onClick={openCreatePortion}>
              <i className="bi bi-plus-lg" />
              Nueva Porción
            </button>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h6>
                <i className="bi bi-box-seam" style={{ color: 'var(--orig-green)' }} />
                Porciones e ingredientes
              </h6>
              {!loadingPortions && (
                <span className="badge-orig badge-green">
                  {portions.length} {portions.length === 1 ? 'porción' : 'porciones'}
                </span>
              )}
            </div>

            {portionActionError && (
              <div style={{ padding: '14px 20px 0' }}>
                <div className="alert-orig alert-error">
                  <i className="bi bi-exclamation-triangle-fill" />
                  {portionActionError}
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              {loadingPortions ? (
                <LoadingRow label="Cargando porciones..." />
              ) : portionsError ? (
                <ErrorRow message={portionsError} />
              ) : portions.length === 0 ? (
                <EmptyRow icon="bi-box-seam" title="Sin resultados" subtitle="No hay porciones registradas." />
              ) : (
                <table className="orig-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nombre</th>
                      <th>Tipo</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portions.map(p => (
                      <tr key={p.id}>
                        <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>{p.id}</td>
                        <td style={{ fontWeight: 500 }}>{p.name}</td>
                        <td style={{ fontSize: 12.5 }}>
                          {p.unitType
                            ? <span style={{ background: 'var(--orig-teal-dim)', color: 'var(--orig-teal-light)', padding: '2px 9px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{p.unitType}</span>
                            : <span style={{ color: 'var(--orig-text-hint)' }}>—</span>}
                        </td>
                        <td>
                          <span className={`badge-orig ${p.state ? 'badge-green' : 'badge-red'}`}>
                            <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                            {p.state ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => openEditPortion(p)}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Editar porción"
                            >
                              <i className="bi bi-pencil" />
                              Editar
                            </button>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => togglePortionState(p)}
                              disabled={portionBusy}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Cambiar estado"
                            >
                              <i className="bi bi-toggle2-on" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Tab: Inventario */}
      {activeTab === 'inventory' && (
        <>
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-body" style={{ padding: '16px 20px' }}>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-6">
                  <label className="form-label">Stand móvil</label>
                  <select
                    className="form-select"
                    value={selectedStandId}
                    onChange={e => setSelectedStandId(e.target.value)}
                  >
                    <option value="">Seleccione un stand</option>
                    {activeStands.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                {selectedStandId && (
                  <div className="col-12 col-md-6" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn-orig btn-orig-green" onClick={openAddStock}>
                      <i className="bi bi-plus-lg" />
                      Agregar Porción
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!selectedStandId ? (
            <div className="section-card">
              <EmptyRow icon="bi-shop-window" title="Seleccione un stand" subtitle="Elija un stand móvil para ver su inventario de porciones." />
            </div>
          ) : (
            <div className="section-card">
              <div className="section-header">
                <h6>
                  <i className="bi bi-clipboard-data" style={{ color: 'var(--orig-green)' }} />
                  Inventario — {selectedStand?.name}
                </h6>
                {!loadingStock && (
                  <span className="badge-orig badge-green">
                    {stock.length} {stock.length === 1 ? 'porción' : 'porciones'}
                  </span>
                )}
              </div>

              <div style={{ overflowX: 'auto' }}>
                {loadingStock ? (
                  <LoadingRow label="Cargando inventario..." />
                ) : stockError ? (
                  <ErrorRow message={stockError} />
                ) : stock.length === 0 ? (
                  <EmptyRow icon="bi-inbox" title="Sin porciones" subtitle="Este stand aún no tiene porciones en inventario." />
                ) : (
                  <table className="orig-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Porción</th>
                        <th>Stock</th>
                        <th>Stock mínimo</th>
                        <th style={{ textAlign: 'center' }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stock.map((item, idx) => {
                        const below = Number(item.stockMin) > 0 && Number(item.stock) <= Number(item.stockMin);
                        return (
                          <tr key={item.id}>
                            <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>{idx + 1}</td>
                            <td>
                              <div style={{ fontWeight: 500 }}>{item.portion?.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>{item.portion?.unitType}</div>
                            </td>
                            <td>
                              <span style={{ fontSize: 14, fontWeight: 700, color: below ? 'var(--orig-red-light)' : 'var(--orig-text)' }}>
                                {item.stock}
                              </span>
                            </td>
                            <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{item.stockMin}</td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                className="btn-orig btn-orig-outline"
                                onClick={() => openEditStock(item)}
                                style={{ padding: '5px 12px', fontSize: 12 }}
                                title="Editar porción"
                              >
                                <i className="bi bi-pencil" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Tab: Abastecimientos */}
      {activeTab === 'supplies' && (
        <>
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-body" style={{ padding: '16px 20px' }}>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-6">
                  <label className="form-label">Stand móvil</label>
                  <select
                    className="form-select"
                    value={selectedStandId}
                    onChange={e => setSelectedStandId(e.target.value)}
                  >
                    <option value="">Seleccione un stand</option>
                    {activeStands.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                {selectedStandId && (
                  <div className="col-12 col-md-6" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn-orig btn-orig-green" onClick={() => setShowSupplyForm(true)}>
                      <i className="bi bi-plus-lg" />
                      Registrar Abastecimiento
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!selectedStandId ? (
            <div className="section-card">
              <EmptyRow icon="bi-shop-window" title="Seleccione un stand" subtitle="Elija un stand móvil para ver su historial de abastecimientos." />
            </div>
          ) : (
            <div className="section-card">
              <div className="section-header">
                <h6>
                  <i className="bi bi-truck" style={{ color: 'var(--orig-green)' }} />
                  Abastecimientos — {selectedStand?.name}
                </h6>
                {!loadingSupplies && (
                  <span className="badge-orig badge-green">
                    {supplies.length} {supplies.length === 1 ? 'registro' : 'registros'}
                  </span>
                )}
              </div>

              <div style={{ overflowX: 'auto' }}>
                {loadingSupplies ? (
                  <LoadingRow label="Cargando abastecimientos..." />
                ) : suppliesError ? (
                  <ErrorRow message={suppliesError} />
                ) : supplies.length === 0 ? (
                  <EmptyRow icon="bi-truck" title="Sin abastecimientos" subtitle="Este stand aún no tiene abastecimientos registrados." />
                ) : (
                  <table className="orig-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Fecha</th>
                        <th>Porciones</th>
                        <th>Observaciones</th>
                        <th>Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplies.map(s => {
                        const totalQty = (s.details || []).reduce((sum, d) => sum + (Number(d.quantity) || 0), 0);
                        return (
                          <tr key={s.id}>
                            <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>{s.id}</td>
                            <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{s.date?.slice(0, 10)}</td>
                            <td>
                              <div style={{ fontWeight: 500 }}>
                                {(s.details || []).length} {(s.details || []).length === 1 ? 'línea' : 'líneas'}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--orig-amber-light)' }}>{totalQty} en total</div>
                            </td>
                            <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.observations || '—'}
                            </td>
                            <td>
                              <button
                                className="btn-orig btn-orig-outline"
                                onClick={() => setViewingSupply(s)}
                                style={{ padding: '5px 12px', fontSize: 12 }}
                                title="Ver detalle del abastecimiento"
                              >
                                <i className="bi bi-eye" />
                                Ver
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal: crear/editar stand móvil */}
      {showStandForm && (
        <MobileStandForm mobileStand={editingStand} onCancel={closeStandForm} onSaved={handleStandSaved} />
      )}

      {/* Modal: crear/editar producto del menú */}
      {showMenuForm && (
        <MenuProductForm menuProduct={editingMenuProduct} onCancel={closeMenuForm} onSaved={handleMenuSaved} />
      )}

      {/* Modal: administrar receta del producto */}
      {recipeProduct && (
        <MenuProductRecipeModal menuProduct={recipeProduct} onClose={() => setRecipeProduct(null)} />
      )}

      {/* Modal: crear/editar porción */}
      {showPortionForm && (
        <PortionForm portion={editingPortion} onCancel={closePortionForm} onSaved={handlePortionSaved} />
      )}

      {/* Modal: agregar/editar porción en inventario */}
      {showStockModal && selectedStandId && (
        <MobileStandPortionModal
          mobileStandId={Number(selectedStandId)}
          existing={editingStock}
          onClose={closeStockModal}
          onSaved={handleStockSaved}
          onManagePortions={() => { closeStockModal(); setActiveTab('portions'); }}
        />
      )}

      {/* Modal: registrar abastecimiento */}
      {showSupplyForm && selectedStandId && (
        <PortionSupplyFormModal
          mobileStandId={Number(selectedStandId)}
          onClose={() => setShowSupplyForm(false)}
          onSaved={handleSupplySaved}
        />
      )}

      {/* Modal: detalle de abastecimiento */}
      {viewingSupply && (
        <PortionSupplyDetalleModal
          portionSupply={viewingSupply}
          onClose={() => setViewingSupply(null)}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
